import { useEffect, useMemo, useState } from "react";
import { AtSign, Loader2, Search, X } from "lucide-react";
import { Badge } from "@/v5/components/ui/badge";
import { Button } from "@/v5/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import {
  getRecipientOptions,
  type RecipientPickerOption,
} from "@/services/communications.service";
import { cn } from "@/v5/lib/utils";

export type RecipientOption = RecipientPickerOption;

interface RecipientPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  planId?: string | number | null;
  /** Compact rendering for inline composers: icon-only trigger, no inline chips. */
  compact?: boolean;
  className?: string;
  disabled?: boolean;
}

export interface OptionSection {
  id: "planTokens" | "groups" | "users";
  heading: string;
  items: RecipientOption[];
}

const optionsCache = new Map<string, OptionSection[]>();

function cacheKey(planId: string | number | null | undefined): string {
  return planId ? `plan:${planId}` : "noplan";
}

function flatten(sections: OptionSection[]): RecipientOption[] {
  return sections.flatMap((s) => s.items);
}

export function filterSections(sections: OptionSection[], query: string): OptionSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter((opt) =>
        opt.label.toLowerCase().includes(q) ||
        (opt.title ?? "").toLowerCase().includes(q),
      ),
    }))
    .filter((s) => s.items.length > 0);
}

function normalizePlanId(planId: string | number | null | undefined): number | null {
  if (planId === null || planId === undefined || planId === "") return null;
  const n = typeof planId === "number" ? planId : Number(planId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Shared loader for the picker option set. Reads from the module-level
 * `optionsCache` first and only fetches when nothing is cached for the
 * given planId. Multiple consumers (RecipientPicker + RecipientChips)
 * end up sharing the same cache so the API is called at most once per
 * planId per page load.
 */
export function useRecipientSections(
  planId: string | number | null | undefined,
  enabled: boolean,
): { sections: OptionSection[]; loading: boolean; error: string | null } {
  const [sections, setSections] = useState<OptionSection[]>(
    () => optionsCache.get(cacheKey(planId)) ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Read the cache BEFORE the enabled guard. A consumer that never triggers
    // a fetch of its own still has to put names on chips for tokens that were
    // added somewhere else — the inline "@" menu fills this cache, and the
    // dialog may never be opened at all. Without this the chips fall back to
    // the raw token and render as "role_5" / "12".
    const key = cacheKey(planId);
    const cached = optionsCache.get(key);
    if (cached && cached.length > 0) {
      setSections(cached);
      return;
    }

    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getRecipientOptions(normalizePlanId(planId))
      .then((data) => {
        if (cancelled) return;
        const safe = data ?? { planTokens: [], groups: [], users: [] };
        // Annotate before .filter(): the contextual type does not survive the
        // call, so the three `id` literals would widen to `string`.
        const all: OptionSection[] = [
          { id: "planTokens", heading: "Plan", items: safe.planTokens ?? [] },
          { id: "groups", heading: "User Groups", items: safe.groups ?? [] },
          { id: "users", heading: "Users", items: safe.users ?? [] },
        ];
        const next = all.filter((s) => s.items.length > 0);
        optionsCache.set(key, next);
        setSections(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, planId]);

  return { sections, loading, error };
}

function useOptionByToken(sections: OptionSection[]): Map<string, RecipientOption> {
  return useMemo(() => {
    const map = new Map<string, RecipientOption>();
    for (const opt of flatten(sections)) map.set(opt.token, opt);
    return map;
  }, [sections]);
}

interface RecipientChipsProps {
  value: string[];
  onChange: (next: string[]) => void;
  planId?: string | number | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Standalone chip list for the selected recipient tokens. Use this when
 * the caller wants the chips rendered separately from the trigger (e.g.
 * above an inline composer row).
 */
export function RecipientChips({
  value,
  onChange,
  planId,
  disabled,
  className,
}: RecipientChipsProps) {
  // Only fetch if the cache doesn't already have entries to label the chips.
  // Any token at all means a name has to be resolved for it. The hook checks
  // the cache before fetching, so a warm cache costs nothing here.
  const { sections } = useRecipientSections(planId, value.length > 0);
  const optionByToken = useOptionByToken(sections);

  if (value.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {value.map((token) => {
        const opt = optionByToken.get(token);
        const label = opt?.label ?? token;
        return (
          <Badge
            key={token}
            variant="secondary"
            className="gap-1 pr-1 pl-2 py-0.5"
          >
            <span className="truncate max-w-[14rem]" title={label}>{label}</span>
            <button
              type="button"
              onClick={() => onChange(value.filter((t) => t !== token))}
              aria-label={`Remove ${label}`}
              className="ml-0.5 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  );
}

export function RecipientPicker({
  value,
  onChange,
  planId,
  compact = false,
  className,
  disabled,
}: RecipientPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Enabled by the dialog OR by simply holding tokens: they may have come from
  // the inline "@" menu, in which case this dialog is never opened and the
  // chips would otherwise show raw tokens.
  const { sections, loading, error } = useRecipientSections(
    planId,
    open || value.length > 0,
  );
  const optionByToken = useOptionByToken(sections);
  const filtered = useMemo(() => filterSections(sections, query), [sections, query]);

  const selected = value;
  const count = selected.length;

  const toggle = (token: string) => {
    if (selected.includes(token)) {
      onChange(selected.filter((t) => t !== token));
    } else {
      onChange([...selected, token]);
    }
  };

  const remove = (token: string) => {
    onChange(selected.filter((t) => t !== token));
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const iconTrigger = (
    <Button
      variant="ghost"
      size="icon"
      type="button"
      className="h-9 w-9 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-800 flex-shrink-0 relative"
      title={count > 0 ? `Tagged: ${count}` : "Tag users or groups"}
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      <AtSign className="h-4.5 w-4.5" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
          {count}
        </span>
      )}
    </Button>
  );

  const fullTrigger = (
    <Button
      type="button"
      variant="outline"
      size="default"
      disabled={disabled}
      onClick={() => setOpen(true)}
      className="gap-1.5"
    >
      <AtSign className="h-3.5 w-3.5" />
      {count === 0 ? "Tag users or groups" : `${count} selected`}
    </Button>
  );

  const inlineChipList = !compact && count > 0 ? (
    <div className="flex flex-wrap items-center gap-1">
      {selected.map((token) => {
        const opt = optionByToken.get(token);
        const label = opt?.label ?? token;
        return (
          <Badge
            key={token}
            variant="secondary"
            className="gap-1 pr-1 pl-2 py-0.5"
          >
            <span className="truncate max-w-[14rem]" title={label}>{label}</span>
            <button
              type="button"
              onClick={() => remove(token)}
              aria-label={`Remove ${label}`}
              className="ml-0.5 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  ) : null;

  const dialog = (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AtSign className="h-5 w-5 text-primary" />
            Tag users and groups
          </DialogTitle>
          <DialogDescription>
            They will be notified by in-app, email, and SMS based on each user&apos;s preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users and groups..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="rounded-sm p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-4 text-sm text-destructive">{error}</div>
          )}
          {!loading && !error && sections.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No users or groups available.
            </div>
          )}
          {!loading && !error && sections.length > 0 && filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No matches for &ldquo;{query}&rdquo;.
            </div>
          )}
          {!loading && !error && filtered.map((section) => (
            <div key={section.id}>
              <div className="sticky top-0 bg-muted/80 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
                {section.heading}
              </div>
              {section.items.map((opt) => {
                const checked = selected.includes(opt.token);
                return (
                  <button
                    key={opt.token}
                    type="button"
                    onClick={() => toggle(opt.token)}
                    className={cn(
                      "flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-accent border-b border-border/40 last:border-b-0",
                      checked && "bg-primary/5",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-foreground">{opt.label}</div>
                      {opt.title && (
                        <div className="truncate text-xs text-muted-foreground">{opt.title}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-muted-foreground">
            {count === 0
              ? "Nothing selected yet."
              : `${count} selected.`}
          </div>
          <Button type="button" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Compact (inline composer): icon-only trigger + dialog. Caller renders chips
  // separately via <RecipientChips /> so they can be placed wherever in the UI.
  if (compact) {
    return (
      <>
        <span className={cn("inline-flex", className)}>{iconTrigger}</span>
        {dialog}
      </>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {fullTrigger}
      {inlineChipList}
      {dialog}
    </div>
  );
}
